import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { 
  Upload, 
  Download, 
  FileSpreadsheet, 
  AlertCircle, 
  CheckCircle2, 
  XCircle,
  Loader2,
  FileText,
  Package,
  Users,
  IndianRupee,
  AlertTriangle,
  ArrowLeft,
  Trash2
} from 'lucide-react';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

var API = process.env.REACT_APP_BACKEND_URL + '/api';

// File Upload Zone Component
function FileUploadZone(props) {
  var onFileSelect = props.onFileSelect;
  var uploading = props.uploading;
  var selectedFile = props.selectedFile;
  var onClear = props.onClear;
  
  var dragActiveState = React.useState(false);
  var dragActive = dragActiveState[0];
  var setDragActive = dragActiveState[1];
  
  var fileInputRef = React.useRef(null);
  
  function handleDrag(e) {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }
  
  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      onFileSelect(e.dataTransfer.files[0]);
    }
  }
  
  function handleChange(e) {
    if (e.target.files && e.target.files[0]) {
      onFileSelect(e.target.files[0]);
    }
  }
  
  if (selectedFile) {
    return React.createElement('div', { className: 'border-2 border-green-300 bg-green-50 rounded-lg p-6' },
      React.createElement('div', { className: 'flex items-center justify-between' },
        React.createElement('div', { className: 'flex items-center gap-3' },
          React.createElement(FileSpreadsheet, { className: 'w-10 h-10 text-green-600' }),
          React.createElement('div', null,
            React.createElement('p', { className: 'font-medium text-green-800' }, selectedFile.name),
            React.createElement('p', { className: 'text-sm text-green-600' }, 
              (selectedFile.size / 1024).toFixed(1) + ' KB'
            )
          )
        ),
        React.createElement(Button, {
          variant: 'ghost',
          size: 'sm',
          onClick: onClear,
          disabled: uploading,
          className: 'text-red-500 hover:text-red-700'
        },
          React.createElement(Trash2, { className: 'w-4 h-4' })
        )
      )
    );
  }
  
  return React.createElement('div', {
    className: 'border-2 border-dashed rounded-lg p-8 text-center transition-colors ' + 
      (dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'),
    onDragEnter: handleDrag,
    onDragLeave: handleDrag,
    onDragOver: handleDrag,
    onDrop: handleDrop
  },
    React.createElement('input', {
      ref: fileInputRef,
      type: 'file',
      accept: '.xlsx,.xls,.csv',
      onChange: handleChange,
      className: 'hidden'
    }),
    React.createElement(Upload, { className: 'w-12 h-12 text-gray-400 mx-auto mb-4' }),
    React.createElement('p', { className: 'text-lg font-medium text-gray-700 mb-2' },
      'Drag & drop Excel file here'
    ),
    React.createElement('p', { className: 'text-sm text-gray-500 mb-4' },
      'or click to browse (supports .xlsx, .xls, .csv)'
    ),
    React.createElement(Button, {
      variant: 'outline',
      onClick: function() { fileInputRef.current.click(); },
      disabled: uploading
    }, 'Select File')
  );
}

// Validation Results Component
function ValidationResults(props) {
  var results = props.results;
  var invoicePreview = props.invoicePreview;
  
  if (!results) return null;
  
  return React.createElement('div', { className: 'space-y-4' },
    // Summary Stats
    React.createElement('div', { className: 'grid grid-cols-4 gap-4' },
      React.createElement(Card, { className: 'bg-slate-50' },
        React.createElement(CardContent, { className: 'p-4' },
          React.createElement('div', { className: 'flex items-center gap-2' },
            React.createElement(FileText, { className: 'w-5 h-5 text-slate-600' }),
            React.createElement('span', { className: 'text-sm text-slate-600' }, 'Total Rows')
          ),
          React.createElement('p', { className: 'text-2xl font-bold mt-1' }, results.total_rows)
        )
      ),
      React.createElement(Card, { className: 'bg-green-50' },
        React.createElement(CardContent, { className: 'p-4' },
          React.createElement('div', { className: 'flex items-center gap-2' },
            React.createElement(CheckCircle2, { className: 'w-5 h-5 text-green-600' }),
            React.createElement('span', { className: 'text-sm text-green-600' }, 'Valid')
          ),
          React.createElement('p', { className: 'text-2xl font-bold text-green-700 mt-1' }, results.valid_rows)
        )
      ),
      React.createElement(Card, { className: 'bg-red-50' },
        React.createElement(CardContent, { className: 'p-4' },
          React.createElement('div', { className: 'flex items-center gap-2' },
            React.createElement(XCircle, { className: 'w-5 h-5 text-red-600' }),
            React.createElement('span', { className: 'text-sm text-red-600' }, 'Errors')
          ),
          React.createElement('p', { className: 'text-2xl font-bold text-red-700 mt-1' }, results.error_rows)
        )
      ),
      React.createElement(Card, { className: 'bg-blue-50' },
        React.createElement(CardContent, { className: 'p-4' },
          React.createElement('div', { className: 'flex items-center gap-2' },
            React.createElement(IndianRupee, { className: 'w-5 h-5 text-blue-600' }),
            React.createElement('span', { className: 'text-sm text-blue-600' }, 'Invoices')
          ),
          React.createElement('p', { className: 'text-2xl font-bold text-blue-700 mt-1' }, results.invoices_to_create)
        )
      )
    ),
    
    // Invoice Preview
    invoicePreview && invoicePreview.length > 0 && React.createElement(Card, null,
      React.createElement(CardHeader, { className: 'pb-2' },
        React.createElement(CardTitle, { className: 'text-base' }, 'Invoices to Create')
      ),
      React.createElement(CardContent, null,
        React.createElement('div', { className: 'space-y-3' },
          invoicePreview.map(function(inv, idx) {
            return React.createElement('div', { 
              key: idx, 
              className: 'p-3 border rounded-lg bg-slate-50' 
            },
              React.createElement('div', { className: 'flex justify-between items-start' },
                React.createElement('div', null,
                  React.createElement('p', { className: 'font-medium' }, inv.customer_name),
                  React.createElement('p', { className: 'text-sm text-gray-500' }, 
                    inv.invoice_date + ' • ' + inv.items.length + ' items • ' + inv.payment_mode
                  )
                ),
                React.createElement('p', { className: 'font-bold text-lg' }, 
                  '₹' + inv.grand_total.toFixed(2)
                )
              )
            );
          })
        )
      )
    ),
    
    // Validation Details Table
    React.createElement(Card, null,
      React.createElement(CardHeader, { className: 'pb-2 flex flex-row items-center justify-between gap-3' },
        React.createElement(CardTitle, { className: 'text-base' }, 'Row-by-Row Validation'),
        results.error_rows > 0 && React.createElement(Button, {
          size: 'sm',
          variant: 'outline',
          'data-testid': 'export-validation-errors-btn',
          onClick: function() {
            try {
              var errorRows = (results.validated_items || []).filter(function(r) { return !r.is_valid; });
              if (!errorRows.length) {
                toast.info('No error rows to export');
                return;
              }
              var sheetData = errorRows.map(function(r) {
                return {
                  'Row #': r.row_number,
                  'Status': 'ERROR',
                  'Customer ID': r.customer_id || '',
                  'Customer Name': r.customer_name || '',
                  'Invoice Date': r.invoice_date || '',
                  'Payment Mode': r.payment_mode || '',
                  'Due Date': r.due_date || '',
                  'Product SKU': r.product_sku || '',
                  'Product Name': r.product_name || '',
                  'Quantity': r.quantity || 0,
                  'Unit': r.unit || '',
                  'Available Stock': r.available_stock !== undefined ? r.available_stock : '',
                  'Unit Price (Excl GST)': r.unit_price || '',
                  'GST %': r.gst_percentage !== undefined ? r.gst_percentage : '',
                  'Discount %': r.discount_percent !== undefined ? r.discount_percent : '',
                  'Taxable Amount': r.taxable_amount || '',
                  'GST Amount': r.gst_amount || '',
                  'Line Total': r.line_total || '',
                  'Errors': (r.errors || []).join(' | '),
                  'Warnings': (r.warnings || []).join(' | '),
                };
              });
              var ws = XLSX.utils.json_to_sheet(sheetData);
              // Tune column widths so long error strings are readable.
              ws['!cols'] = [
                { wch: 6 }, { wch: 8 }, { wch: 18 }, { wch: 22 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
                { wch: 14 }, { wch: 26 }, { wch: 8 }, { wch: 8 }, { wch: 10 },
                { wch: 14 }, { wch: 8 }, { wch: 10 }, { wch: 14 }, { wch: 12 }, { wch: 12 },
                { wch: 60 }, { wch: 50 },
              ];
              var wb = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(wb, ws, 'Validation Errors');
              var stamp = new Date().toISOString().slice(0, 10);
              XLSX.writeFile(wb, 'bulk_invoice_errors_' + stamp + '.xlsx');
              toast.success('Exported ' + errorRows.length + ' error row' + (errorRows.length === 1 ? '' : 's'));
            } catch (e) {
              toast.error('Failed to export errors');
            }
          }
        },
          React.createElement(Download, { className: 'w-4 h-4 mr-1' }),
          'Export Errors (' + results.error_rows + ')'
        )
      ),
      React.createElement(CardContent, null,
        React.createElement('div', { className: 'overflow-auto max-h-[400px]' },
          React.createElement('table', { className: 'w-full text-sm' },
            React.createElement('thead', { className: 'bg-slate-100 sticky top-0' },
              React.createElement('tr', null,
                React.createElement('th', { className: 'p-2 text-left' }, 'Row'),
                React.createElement('th', { className: 'p-2 text-left' }, 'Status'),
                React.createElement('th', { className: 'p-2 text-left' }, 'Customer'),
                React.createElement('th', { className: 'p-2 text-left' }, 'Product'),
                React.createElement('th', { className: 'p-2 text-right' }, 'Qty'),
                React.createElement('th', { className: 'p-2 text-right' }, 'Stock'),
                React.createElement('th', { className: 'p-2 text-right' }, 'Total'),
                React.createElement('th', { className: 'p-2 text-left' }, 'Issues')
              )
            ),
            React.createElement('tbody', null,
              results.validated_items.map(function(item) {
                return React.createElement('tr', { 
                  key: item.row_number,
                  className: item.is_valid ? 'border-b' : 'border-b bg-red-50'
                },
                  React.createElement('td', { className: 'p-2' }, item.row_number),
                  React.createElement('td', { className: 'p-2' },
                    item.is_valid 
                      ? React.createElement(Badge, { className: 'bg-green-100 text-green-700' }, 'Valid')
                      : React.createElement(Badge, { variant: 'destructive' }, 'Error')
                  ),
                  React.createElement('td', { className: 'p-2 max-w-[120px] truncate' }, 
                    item.customer_name || '-'
                  ),
                  React.createElement('td', { className: 'p-2 max-w-[120px] truncate' }, 
                    item.product_name || '-'
                  ),
                  React.createElement('td', { className: 'p-2 text-right' }, item.quantity),
                  React.createElement('td', { className: 'p-2 text-right' },
                    item.available_stock < item.quantity
                      ? React.createElement('span', { className: 'text-red-600 font-medium' }, item.available_stock)
                      : item.available_stock
                  ),
                  React.createElement('td', { className: 'p-2 text-right font-medium' }, 
                    '₹' + (item.line_total || 0).toFixed(2)
                  ),
                  React.createElement('td', { className: 'p-2' },
                    React.createElement('div', { className: 'space-y-1' },
                      item.errors.map(function(err, i) {
                        return React.createElement('p', { 
                          key: 'e' + i, 
                          className: 'text-xs text-red-600' 
                        }, '❌ ' + err);
                      }),
                      item.warnings.map(function(warn, i) {
                        return React.createElement('p', { 
                          key: 'w' + i, 
                          className: 'text-xs text-amber-600' 
                        }, '⚠️ ' + warn);
                      })
                    )
                  )
                );
              })
            )
          )
        )
      )
    )
  );
}

// Import Results Component
function ImportResults(props) {
  var results = props.results;
  var onClose = props.onClose;
  
  if (!results) return null;
  
  return React.createElement(Card, { className: results.success ? 'border-green-300' : 'border-amber-300' },
    React.createElement(CardHeader, null,
      React.createElement(CardTitle, { className: 'flex items-center gap-2' },
        results.success 
          ? React.createElement(CheckCircle2, { className: 'w-6 h-6 text-green-600' })
          : React.createElement(AlertTriangle, { className: 'w-6 h-6 text-amber-600' }),
        results.success ? 'Import Successful!' : 'Import Completed with Issues'
      )
    ),
    React.createElement(CardContent, { className: 'space-y-4' },
      React.createElement('div', { className: 'grid grid-cols-3 gap-4' },
        React.createElement('div', { className: 'p-4 bg-green-50 rounded-lg text-center' },
          React.createElement('p', { className: 'text-3xl font-bold text-green-700' }, results.created_count),
          React.createElement('p', { className: 'text-sm text-green-600' }, 'Invoices Created')
        ),
        React.createElement('div', { className: 'p-4 bg-red-50 rounded-lg text-center' },
          React.createElement('p', { className: 'text-3xl font-bold text-red-700' }, results.failed_count),
          React.createElement('p', { className: 'text-sm text-red-600' }, 'Failed')
        ),
        React.createElement('div', { className: 'p-4 bg-blue-50 rounded-lg text-center' },
          React.createElement('p', { className: 'text-3xl font-bold text-blue-700' }, 
            results.finalized ? 'Finalized' : 'Draft'
          ),
          React.createElement('p', { className: 'text-sm text-blue-600' }, 'Status')
        )
      ),
      
      // Created Invoices List
      results.created_invoices.length > 0 && React.createElement('div', null,
        React.createElement('h4', { className: 'font-medium mb-2' }, 'Created Invoices:'),
        React.createElement('div', { className: 'space-y-2 max-h-[200px] overflow-auto' },
          results.created_invoices.map(function(inv) {
            return React.createElement('div', { 
              key: inv.invoice_id,
              className: 'flex justify-between items-center p-2 bg-slate-50 rounded'
            },
              React.createElement('div', null,
                React.createElement('span', { className: 'font-mono text-sm font-medium' }, inv.invoice_number),
                React.createElement('span', { className: 'text-gray-500 ml-2' }, inv.customer_name)
              ),
              React.createElement('div', { className: 'flex items-center gap-3' },
                React.createElement(Badge, { 
                  className: inv.status === 'finalized' ? 'bg-green-100 text-green-700' : 'bg-slate-100'
                }, inv.status),
                React.createElement('span', { className: 'font-bold' }, '₹' + inv.grand_total.toFixed(2))
              )
            );
          })
        )
      ),
      
      // Failed Invoices
      results.failed_invoices.length > 0 && React.createElement('div', null,
        React.createElement('h4', { className: 'font-medium mb-2 text-red-700' }, 'Failed:'),
        React.createElement('div', { className: 'space-y-2' },
          results.failed_invoices.map(function(fail, idx) {
            return React.createElement('div', { 
              key: idx,
              className: 'p-2 bg-red-50 rounded border border-red-200'
            },
              React.createElement('span', { className: 'font-medium' }, fail.customer_name || fail.customer_id),
              React.createElement('span', { className: 'text-red-600 ml-2' }, fail.error)
            );
          })
        )
      ),
      
      React.createElement('div', { className: 'flex justify-end pt-4' },
        React.createElement(Button, { onClick: onClose }, 'Done')
      )
    )
  );
}

// Main Component
function BulkInvoiceUpload() {
  var stepState = React.useState('upload'); // upload, validate, import, done
  var step = stepState[0];
  var setStep = stepState[1];
  
  var loadingState = React.useState(false);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  
  var selectedFileState = React.useState(null);
  var selectedFile = selectedFileState[0];
  var setSelectedFile = selectedFileState[1];
  
  var parsedDataState = React.useState([]);
  var parsedData = parsedDataState[0];
  var setParsedData = parsedDataState[1];
  
  var validationResultState = React.useState(null);
  var validationResult = validationResultState[0];
  var setValidationResult = validationResultState[1];
  
  var importResultState = React.useState(null);
  var importResult = importResultState[0];
  var setImportResult = importResultState[1];
  
  var finalizeState = React.useState(false);
  var finalize = finalizeState[0];
  var setFinalize = finalizeState[1];
  
  function downloadTemplate() {
    var token = localStorage.getItem('token');
    setLoading(true);
    
    axios.get(API + '/invoices/bulk-upload/template', {
      headers: { Authorization: 'Bearer ' + token },
      responseType: 'blob'
    }).then(function(response) {
      var url = window.URL.createObjectURL(new Blob([response.data]));
      var link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Bulk_Invoice_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded!');
    }).catch(function(error) {
      toast.error('Failed to download template');
    }).finally(function() {
      setLoading(false);
    });
  }
  
  function handleFileSelect(file) {
    if (!file) return;
    
    var validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    
    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
      toast.error('Please upload an Excel or CSV file (.xlsx, .xls or .csv)');
      return;
    }
    
    setSelectedFile(file);
    parseExcelFile(file);
  }
  
  function parseExcelFile(file) {
    setLoading(true);
    
    var reader = new FileReader();
    reader.onload = function(e) {
      try {
        var XLSX = window.XLSX;
        if (!XLSX) {
          toast.error('Excel parser not loaded. Please refresh the page.');
          setLoading(false);
          return;
        }
        
        var data = new Uint8Array(e.target.result);
        var workbook = XLSX.read(data, { type: 'array' });
        var sheetName = workbook.SheetNames[0];
        var sheet = workbook.Sheets[sheetName];
        var jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        
        // Skip header row
        var rows = [];
        for (var i = 1; i < jsonData.length; i++) {
          var row = jsonData[i];
          if (!row || row.length === 0 || !row[0]) continue;
          
          rows.push({
            row_number: i + 1,
            customer_id: String(row[0] || '').trim(),
            customer_name: String(row[1] || '').trim(),
            invoice_date: formatExcelDate(row[2]),
            payment_mode: String(row[3] || 'cash').trim().toLowerCase(),
            due_date: formatExcelDate(row[4]),
            product_sku: String(row[5] || '').trim(),
            product_name: String(row[6] || '').trim(),
            quantity: parseFloat(row[7]) || 0,
            unit: String(row[8] || 'PCS').trim().toUpperCase(),
            unit_price: parseFloat(row[9]) || 0,
            gst_percentage: parseFloat(row[10]) || 0,
            discount_percent: parseFloat(row[11]) || 0
          });
        }
        
        if (rows.length === 0) {
          toast.error('No data found in Excel file');
          setSelectedFile(null);
          setLoading(false);
          return;
        }
        
        setParsedData(rows);
        toast.success('Parsed ' + rows.length + ' rows from Excel');
        setLoading(false);
        
      } catch (error) {
        toast.error('Failed to parse Excel file');
        setSelectedFile(null);
        setLoading(false);
      }
    };
    
    reader.onerror = function() {
      toast.error('Failed to read file');
      setLoading(false);
    };
    
    reader.readAsArrayBuffer(file);
  }
  
  function formatExcelDate(value) {
    if (!value) return '';
    
    // If it's already a string in date format
    if (typeof value === 'string') {
      // Try to parse common formats
      var match = value.match(/(\d{4})-(\d{2})-(\d{2})/);
      if (match) return value;
      
      match = value.match(/(\d{2})\/(\d{2})\/(\d{4})/);
      if (match) return match[3] + '-' + match[2] + '-' + match[1];
      
      return value;
    }
    
    // If it's an Excel serial number
    if (typeof value === 'number') {
      var date = new Date((value - 25569) * 86400 * 1000);
      var year = date.getFullYear();
      var month = String(date.getMonth() + 1).padStart(2, '0');
      var day = String(date.getDate()).padStart(2, '0');
      return year + '-' + month + '-' + day;
    }
    
    return '';
  }
  
  function validateData() {
    if (parsedData.length === 0) {
      toast.error('No data to validate');
      return;
    }
    
    setLoading(true);
    var token = localStorage.getItem('token');
    
    axios.post(API + '/invoices/bulk-upload/validate', parsedData, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(response) {
      setValidationResult(response.data);
      setStep('validate');
      
      if (response.data.error_rows > 0) {
        toast.warning(response.data.error_rows + ' rows have errors. Please review.');
      } else {
        toast.success('All ' + response.data.valid_rows + ' rows validated successfully!');
      }
    }).catch(function(error) {
      toast.error(error.response?.data?.detail || 'Validation failed');
    }).finally(function() {
      setLoading(false);
    });
  }
  
  function importInvoices() {
    if (!validationResult || validationResult.valid_rows === 0) {
      toast.error('No valid rows to import');
      return;
    }
    
    setLoading(true);
    var token = localStorage.getItem('token');
    
    axios.post(API + '/invoices/bulk-upload/import?finalize=' + finalize, 
      validationResult.validated_items.filter(function(item) { return item.is_valid; }),
      { headers: { Authorization: 'Bearer ' + token } }
    ).then(function(response) {
      setImportResult(response.data);
      setStep('done');
      
      if (response.data.success) {
        toast.success(response.data.created_count + ' invoices created successfully!');
      } else {
        toast.warning('Import completed with some failures');
      }
    }).catch(function(error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    }).finally(function() {
      setLoading(false);
    });
  }
  
  function resetUpload() {
    setStep('upload');
    setSelectedFile(null);
    setParsedData([]);
    setValidationResult(null);
    setImportResult(null);
    setFinalize(false);
  }
  
  // Step: Done - Show Results
  if (step === 'done') {
    return React.createElement('div', { className: 'space-y-4', 'data-testid': 'bulk-upload-results' },
      React.createElement(ImportResults, {
        results: importResult,
        onClose: resetUpload
      })
    );
  }
  
  // Step: Validate - Show Validation Results
  if (step === 'validate') {
    return React.createElement('div', { className: 'space-y-4', 'data-testid': 'bulk-upload-validate' },
      React.createElement('div', { className: 'flex items-center justify-between' },
        React.createElement(Button, {
          variant: 'ghost',
          onClick: function() { setStep('upload'); },
          className: 'gap-2'
        },
          React.createElement(ArrowLeft, { className: 'w-4 h-4' }),
          'Back to Upload'
        ),
        React.createElement('div', { className: 'flex items-center gap-4' },
          React.createElement('div', { className: 'flex items-center gap-2' },
            React.createElement(Switch, {
              id: 'finalize',
              checked: finalize,
              onCheckedChange: setFinalize
            }),
            React.createElement(Label, { htmlFor: 'finalize', className: 'text-sm' },
              'Finalize & Deduct Stock'
            )
          ),
          React.createElement(Button, {
            onClick: importInvoices,
            disabled: loading || !validationResult || validationResult.valid_rows === 0,
            className: 'gap-2 bg-green-600 hover:bg-green-700'
          },
            loading 
              ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' })
              : React.createElement(CheckCircle2, { className: 'w-4 h-4' }),
            'Import ' + (validationResult?.invoices_to_create || 0) + ' Invoices'
          )
        )
      ),
      
      React.createElement(ValidationResults, {
        results: validationResult,
        invoicePreview: validationResult?.invoice_preview
      })
    );
  }
  
  // Step: Upload
  return React.createElement('div', { className: 'space-y-6', 'data-testid': 'bulk-upload-page' },
    // Instructions Card
    React.createElement(Card, null,
      React.createElement(CardHeader, null,
        React.createElement(CardTitle, { className: 'flex items-center gap-2' },
          React.createElement(FileSpreadsheet, { className: 'w-5 h-5 text-blue-600' }),
          'Bulk Invoice Upload'
        ),
        React.createElement(CardDescription, null,
          'Upload multiple invoices at once using an Excel file. Download the template, fill in your data, and import.'
        )
      ),
      React.createElement(CardContent, { className: 'space-y-6' },
        // Step 1: Download Template
        React.createElement('div', { className: 'flex items-start gap-4 p-4 bg-blue-50 rounded-lg' },
          React.createElement('div', { className: 'w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold' }, '1'),
          React.createElement('div', { className: 'flex-1' },
            React.createElement('h4', { className: 'font-medium' }, 'Download Excel Template'),
            React.createElement('p', { className: 'text-sm text-gray-600 mb-3' },
              'Start with our template to ensure correct data format. Includes sample data and instructions.'
            ),
            React.createElement(Button, {
              variant: 'outline',
              onClick: downloadTemplate,
              disabled: loading,
              className: 'gap-2'
            },
              loading 
                ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' })
                : React.createElement(Download, { className: 'w-4 h-4' }),
              'Download Template'
            ),
            React.createElement(CsvDownloadButton, { onDownload: downloadTemplate, testId: 'bulk-invoice-template-csv', label: 'Template CSV', size: 'default', className: 'ml-2' })
          )
        ),
        
        // Step 2: Upload File
        React.createElement('div', { className: 'flex items-start gap-4 p-4 bg-slate-50 rounded-lg' },
          React.createElement('div', { className: 'w-8 h-8 bg-slate-600 text-white rounded-full flex items-center justify-center font-bold' }, '2'),
          React.createElement('div', { className: 'flex-1' },
            React.createElement('h4', { className: 'font-medium' }, 'Upload Your File'),
            React.createElement('p', { className: 'text-sm text-gray-600 mb-3' },
              'Fill in the template with your invoice data and upload it here.'
            ),
            React.createElement(FileUploadZone, {
              onFileSelect: handleFileSelect,
              uploading: loading,
              selectedFile: selectedFile,
              onClear: function() { 
                setSelectedFile(null); 
                setParsedData([]); 
              }
            })
          )
        ),
        
        // Parsed Data Preview
        parsedData.length > 0 && React.createElement('div', { className: 'p-4 border rounded-lg' },
          React.createElement('div', { className: 'flex items-center justify-between mb-4' },
            React.createElement('div', null,
              React.createElement('h4', { className: 'font-medium' }, 'Parsed Data Preview'),
              React.createElement('p', { className: 'text-sm text-gray-500' }, 
                parsedData.length + ' rows found'
              )
            ),
            React.createElement(Button, {
              onClick: validateData,
              disabled: loading,
              className: 'gap-2'
            },
              loading 
                ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' })
                : React.createElement(CheckCircle2, { className: 'w-4 h-4' }),
              'Validate Data'
            )
          ),
          React.createElement('div', { className: 'overflow-auto max-h-[300px]' },
            React.createElement('table', { className: 'w-full text-xs' },
              React.createElement('thead', { className: 'bg-slate-100 sticky top-0' },
                React.createElement('tr', null,
                  React.createElement('th', { className: 'p-2 text-left' }, 'Row'),
                  React.createElement('th', { className: 'p-2 text-left' }, 'Customer ID'),
                  React.createElement('th', { className: 'p-2 text-left' }, 'Date'),
                  React.createElement('th', { className: 'p-2 text-left' }, 'Product'),
                  React.createElement('th', { className: 'p-2 text-right' }, 'Qty'),
                  React.createElement('th', { className: 'p-2 text-right' }, 'Price'),
                  React.createElement('th', { className: 'p-2 text-right' }, 'GST%')
                )
              ),
              React.createElement('tbody', null,
                parsedData.slice(0, 20).map(function(row) {
                  return React.createElement('tr', { key: row.row_number, className: 'border-b' },
                    React.createElement('td', { className: 'p-2' }, row.row_number),
                    React.createElement('td', { className: 'p-2 max-w-[100px] truncate' }, row.customer_id),
                    React.createElement('td', { className: 'p-2' }, row.invoice_date),
                    React.createElement('td', { className: 'p-2 max-w-[100px] truncate' }, row.product_sku),
                    React.createElement('td', { className: 'p-2 text-right' }, row.quantity),
                    React.createElement('td', { className: 'p-2 text-right' }, '₹' + row.unit_price),
                    React.createElement('td', { className: 'p-2 text-right' }, row.gst_percentage + '%')
                  );
                })
              )
            ),
            parsedData.length > 20 && React.createElement('p', { className: 'text-center text-sm text-gray-500 py-2' },
              '... and ' + (parsedData.length - 20) + ' more rows'
            )
          )
        ),
        
        // Step 3 Info
        React.createElement('div', { className: 'flex items-start gap-4 p-4 bg-green-50 rounded-lg opacity-60' },
          React.createElement('div', { className: 'w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold' }, '3'),
          React.createElement('div', { className: 'flex-1' },
            React.createElement('h4', { className: 'font-medium' }, 'Validate & Import'),
            React.createElement('p', { className: 'text-sm text-gray-600' },
              'After upload, validate your data to check for errors. Then import to create invoices.'
            )
          )
        )
      )
    )
  );
}

export default BulkInvoiceUpload;
